import type { Campo } from './esquema'

/**
 * Dónde están los errores que devolvió el núcleo, para que no se queden escondidos: la
 * pestaña que los tiene se marca, y el plegable o la fila que los lleva dentro se abre
 * (auditoría del 29-09-2026, M6). Las rutas son las de Payload: «seo.titulo»,
 * «bloques.2.titulo».
 */
export type Errores = Record<string, string>

/** Si la ruta de un error es la de este campo o la de algo que lleva dentro. */
export const esDe = (ruta: string, campo: string): boolean => campo === '' || ruta === campo || ruta.startsWith(`${campo}.`)

/** Si hay algún error en este campo o dentro de él. */
export const hayErroresEn = (errores: Errores, ruta: string): boolean => Object.keys(errores).some((r) => esDe(r, ruta))

/** Si hay algún error en alguno de estos campos (los de un plegable, que no tiene ruta propia). */
export const hayErroresEnAlguno = (errores: Errores, rutas: string[]): boolean => rutas.some((r) => r !== '' && hayErroresEn(errores, r))

/**
 * Las rutas de los campos con nombre que cuelgan de estos, bajando por lo que no tiene
 * nombre propio (filas de maquetación, plegables, pestañas sin nombre): es donde puede
 * caer un error de un plegable, que no tiene ruta suya.
 */
export const rutasDeLosCampos = (campos: Campo[], ruta: string): string[] =>
  campos.flatMap((c) => {
    const unir = (nombre?: string) => (nombre ? (ruta ? `${ruta}.${nombre}` : nombre) : ruta)
    if (c.nombre) return [unir(c.nombre)]
    if (c.tipo === 'tabs') return (c.pestanas ?? []).flatMap((p) => (p.nombre ? [unir(p.nombre)] : rutasDeLosCampos(p.campos, ruta)))
    return rutasDeLosCampos(c.campos ?? [], ruta)
  })

/** Qué pestaña del editor tiene errores: la propia de cada campo (SEO, campos del cliente), y «contenido» todo lo demás. */
export const pestanasConErrores = (errores: Errores, propias: Array<{ clave: string; campos?: string[] }>): Set<string> => {
  const con = new Set<string>()
  for (const ruta of Object.keys(errores)) {
    const propia = propias.find((t) => (t.campos ?? []).some((c) => esDe(ruta, c)))
    con.add(propia ? propia.clave : 'contenido')
  }
  return con
}

/** La primera pestaña (por orden) con errores, o `null`. */
export const primeraConErrores = (orden: string[], con: Set<string>): null | string => orden.find((c) => con.has(c)) ?? null
